import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Table,
    Button,
    Space,
    Typography,
    Row,
    Col,
    Statistic,
    message,
    Tag,
    Descriptions,
    Divider,
    Modal,
    Spin,
    Alert
} from 'antd';
import {
    ArrowLeftOutlined,
    FileTextOutlined,
    BankOutlined,
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    EditOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    SafetyCertificateOutlined,
    EyeOutlined
} from '@ant-design/icons';
import adminApi from '../services/adminApi';
import { ncaLayerService } from '../../services/ncaLayerService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface CompanyDetails {
    id: number;
    name: string;
    bin: string;
    legalAddress: string;
    actualAddress: string;
    contactPhone: string;
    contactEmail: string;
    directorName: string;
    directorIin?: string;
    bankName?: string;
    bankBik?: string;
    bankAccount?: string;
    bankKbe?: string;
    status: string;
    createdAt: string;
    updatedAt?: string;
    contractStats?: {
        totalContracts: number;
        signedContracts: number;
        draftContracts: number;
        pendingContracts: number;
    };
}

interface Contract {
    id: number;
    contractNumber: string;
    companyName: string;
    companyBin: string;
    status: string;
    signedAt: string;
    createdAt: string;
    filePath?: string;
    company?: {
        id: number;
        bin: string;
        name: string;
    };
}

export const CompanyContractsPage: React.FC = () => {
    const { companyId } = useParams<{ companyId: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(null);
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [signingContract, setSigningContract] = useState<Contract | null>(null);
    const [signingModalVisible, setSigningModalVisible] = useState(false);
    const [signingLoading, setSigningLoading] = useState(false);
    const [ncaLayerStatus, setNcaLayerStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
    const [certificates, setCertificates] = useState<any[]>([]);
    const [selectedCertificate, setSelectedCertificate] = useState<string>('');
    const [signaturesModalVisible, setSignaturesModalVisible] = useState(false);
    const [contractSignatures, setContractSignatures] = useState<any[]>([]);
    const [viewingContract, setViewingContract] = useState<Contract | null>(null);

    useEffect(() => {
        if (companyId) {
            loadCompanyData();
        }
        checkNCALayer();
    }, [companyId]);

    const checkNCALayer = async () => {
        setNcaLayerStatus('checking');
        try {
            const isAvailable = await ncaLayerService.isAvailable();
            if (isAvailable) {
                setNcaLayerStatus('available');
                // Загружаем сертификаты
                const certs = await ncaLayerService.getCertificates();
                setCertificates(certs);
                if (certs.length > 0) {
                    // Автоматически выбираем PKCS12 если доступен
                    const pkcs12Cert = certs.find(cert => cert.path === 'PKCS12');
                    setSelectedCertificate(pkcs12Cert?.path || certs[0].path);
                }
            } else {
                setNcaLayerStatus('unavailable');
            }
        } catch (error) {
            console.error('Ошибка проверки NCA Layer:', error);
            setNcaLayerStatus('unavailable');
        }
    };

    const loadCompanyData = async () => {
        setLoading(true);
        try {
            const [detailsResponse, contractsResponse] = await Promise.all([
                adminApi.getCompanyDetails(Number(companyId)),
                adminApi.getCompanySignedContracts(Number(companyId))
            ]);

            if (detailsResponse.data.success && detailsResponse.data.data) {
                setCompanyDetails(detailsResponse.data.data);
            }

            if (contractsResponse.data.success && contractsResponse.data.data) {
                setContracts(contractsResponse.data.data);
            }
        } catch (error: any) {
            message.error('Ошибка загрузки данных компании');
            console.error('Ошибка загрузки данных компании:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSignContract = (contract: Contract) => {
        setSigningContract(contract);
        setSigningModalVisible(true);
    };

    const handleDownloadContract = async (contractId: number) => {
        try {
            const response = await adminApi.downloadContract(contractId);

            if (response.data.success && response.data.data?.downloadUrl) {
                // Открываем ссылку для скачивания в новом окне
                window.open(response.data.data.downloadUrl, '_blank');
            } else {
                message.error('Не удалось получить ссылку для скачивания');
            }
        } catch (error: any) {
            console.error('Ошибка скачивания договора:', error);
            message.error('Ошибка скачивания договора: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleDownloadCmsZip = async (contract: Contract) => {
        try {
            message.loading('Загрузка CMS архива...', 0);
            const blob = await adminApi.downloadCmsZip(contract.id);

            // Создаем URL для blob
            const url = window.URL.createObjectURL(blob);

            // Создаем невидимую ссылку для скачивания
            const link = document.createElement('a');
            link.href = url;
            link.download = `CMS_signatures_${contract.contractNumber}.zip`;
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();

            // Очистка
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.destroy();
            message.success('CMS архив успешно скачан');
        } catch (error: any) {
            message.destroy();
            console.error('Ошибка скачивания CMS архива:', error);
            message.error('Ошибка скачивания CMS архива: ' + (error.response?.data?.message || error.message || 'CMS файлы не найдены'));
        }
    };

    const handleViewSignatures = async (contract: Contract) => {
        try {
            setViewingContract(contract);
            const response = await adminApi.getContractSignatures(contract.id);

            if (response.data.success && response.data.data) {
                setContractSignatures(response.data.data);
                setSignaturesModalVisible(true);
            } else {
                message.error('Не удалось получить информацию о подписях');
            }
        } catch (error: any) {
            console.error('Ошибка получения подписей:', error);
            message.error('Ошибка получения подписей: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleSignWithNCALayer = async () => {
        if (!signingContract) return;

        setSigningLoading(true);
        try {
            // Проверяем доступность NCA Layer
            if (ncaLayerStatus !== 'available') {
                message.error('NCALayer недоступен. Установите NCALayer и запустите его.');
                return;
            }

            // Используем выбранный сертификат или PKCS12 по умолчанию
            const certPath = selectedCertificate || 'PKCS12';

            // Подготавливаем данные для подписания
            const contractData = JSON.stringify({
                contractId: signingContract.id,
                contractNumber: signingContract.contractNumber,
                companyBin: signingContract.companyBin,
                companyName: signingContract.companyName,
                timestamp: new Date().toISOString()
            });

            message.loading('Подписание договора...');

            // Подписываем данные через NCA Layer
            const signatureResult = await ncaLayerService.signData(contractData, certPath);

            // Проверяем, что подпись получена
            if (!signatureResult.signature || signatureResult.signature.trim() === '') {
                throw new Error('Не удалось получить подпись от NCA Layer. Проверьте подключение и сертификат.');
            }

            // Отправляем подпись на бэкенд в правильном формате
            await adminApi.signContractSupplier(signingContract.id, {
                signatureData: signatureResult.signature,
                certificateData: signatureResult.certificate,
                signerInfo: JSON.stringify(signatureResult.signerInfo),
                signerName: signatureResult.signerInfo.commonName,
                organizationName: signatureResult.signerInfo.organization,
                signDate: new Date().toLocaleDateString('ru-RU')
            });

            message.destroy();
            message.success('Договор успешно подписан от имени поставщика');
            setSigningModalVisible(false);
            setSigningContract(null);

            // Обновляем данные
            loadCompanyData();

        } catch (error: any) {
            message.destroy();
            console.error('Ошибка подписания договора:', error);
            message.error('Ошибка подписания договора: ' + (error.message || 'Неизвестная ошибка'));
        } finally {
            setSigningLoading(false);
        }
    };


    const getStatusColor = (status: string) => {
        const colors: { [key: string]: string } = {
            'SIGNED_BY_BUYER': 'green',
            'SIGNED_BY_SUPPLIER': 'blue',
            'SIGNED_BY_BOTH': 'purple',
            'PENDING_SIGNATURE': 'orange',
            'DRAFT': 'gray',
            'CANCELLED': 'red',
            'APPROVED': 'green',
            'PENDING_MODERATION': 'orange',
            'NEEDS_REVISION': 'red'
        };
        return colors[status] || 'default';
    };

    const getStatusText = (status: string) => {
        const texts: { [key: string]: string } = {
            'SIGNED_BY_BUYER': 'Подписан покупателем',
            'SIGNED_BY_SUPPLIER': 'Подписан поставщиком',
            'SIGNED_BY_BOTH': 'Подписан обеими сторонами',
            'PENDING_SIGNATURE': 'Ожидает подписания',
            'DRAFT': 'Черновик',
            'CANCELLED': 'Отменен',
            'APPROVED': 'Одобрен',
            'PENDING_MODERATION': 'На модерации',
            'NEEDS_REVISION': 'Требует доработки'
        };
        return texts[status] || status;
    };

    const contractsColumns = [
        {
            title: 'Номер договора',
            dataIndex: 'contractNumber',
            key: 'contractNumber',
            render: (text: string) => (
                <Text strong>{text}</Text>
            ),
        },
        {
            title: 'Дата создания',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => (
                <div>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(date).format('DD.MM.YYYY HH:mm')}
                </div>
            ),
        },
        {
            title: 'Дата подписания',
            dataIndex: 'signedAt',
            key: 'signedAt',
            render: (date: string) => (
                <div>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(date).format('DD.MM.YYYY HH:mm')}
                </div>
            ),
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={getStatusColor(status)} icon={<CheckCircleOutlined />}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: Contract) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => handleSignContract(record)}
                        disabled={record.status === 'SIGNED_BY_SUPPLIER' || record.status === 'SIGNED_BY_BOTH'}
                    >
                        Подписать от поставщика
                    </Button>
                    <Button
                        type="default"
                        icon={<SafetyCertificateOutlined />}
                        onClick={() => handleViewSignatures(record)}
                    >
                        Подписи
                    </Button>
                    {record.filePath && (
                        <>
                            <Button
                                type="default"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownloadContract(record.id)}
                            >
                                Скачать PDF
                            </Button>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownloadCmsZip(record)}
                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                title="Скачать архив с CMS файлами электронных подписей"
                            >
                                Скачать CMS
                            </Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                    <Text>Загрузка данных компании...</Text>
                </div>
            </div>
        );
    }

    if (!companyDetails) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Text type="danger">Компания не найдена</Text>
                <br />
                <Button type="link" onClick={() => navigate('/admin/client-contracts')}>
                    Вернуться к списку компаний
                </Button>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            {/* Баннер ошибки NCA Layer */}
            {ncaLayerStatus === 'unavailable' && (
                <Alert
                    message="NCA Layer недоступен"
                    description="Проверьте подключение к интернету."
                    type="error"
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginBottom: '24px' }}
                    action={
                        <Button size="small" onClick={checkNCALayer}>
                            Повторить проверку
                        </Button>
                    }
                />
            )}

            {/* Заголовок */}
            <div style={{ marginBottom: '24px' }}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/admin/client-contracts')}
                    style={{ marginBottom: '16px' }}
                >
                    Назад к списку компаний
                </Button>

                <Title level={2}>
                    <BankOutlined style={{ marginRight: '8px' }} />
                    {companyDetails.name}
                </Title>
                <Text type="secondary">
                    БИН: {companyDetails.bin} • Управление договорами компании
                </Text>
            </div>

            {/* Информация о компании */}
            <Card style={{ marginBottom: '24px' }}>
                <Title level={4}>Информация о компании</Title>
                <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label="Название" span={2}>
                        <Text strong>{companyDetails.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="БИН">
                        {companyDetails.bin}
                    </Descriptions.Item>
                    <Descriptions.Item label="Статус">
                        <Tag color={getStatusColor(companyDetails.status)}>
                            {getStatusText(companyDetails.status)}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Юридический адрес" span={2}>
                        {companyDetails.legalAddress}
                    </Descriptions.Item>
                    <Descriptions.Item label="Фактический адрес" span={2}>
                        {companyDetails.actualAddress}
                    </Descriptions.Item>
                    <Descriptions.Item label="Телефон">
                        <PhoneOutlined /> {companyDetails.contactPhone}
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                        <MailOutlined /> {companyDetails.contactEmail}
                    </Descriptions.Item>
                    <Descriptions.Item label="Директор" span={2}>
                        <UserOutlined /> {companyDetails.directorName}
                        {companyDetails.directorIin && ` (ИИН: ${companyDetails.directorIin})`}
                    </Descriptions.Item>

                    {companyDetails.bankName && (
                        <>
                            <Descriptions.Item label="Банк" span={2}>
                                <BankOutlined /> {companyDetails.bankName}
                            </Descriptions.Item>
                            <Descriptions.Item label="БИК">
                                {companyDetails.bankBik}
                            </Descriptions.Item>
                            <Descriptions.Item label="Счет">
                                {companyDetails.bankAccount}
                            </Descriptions.Item>
                            {companyDetails.bankKbe && (
                                <Descriptions.Item label="КБе" span={2}>
                                    {companyDetails.bankKbe}
                                </Descriptions.Item>
                            )}
                        </>
                    )}

                    <Descriptions.Item label="Дата создания">
                        {dayjs(companyDetails.createdAt).format('DD.MM.YYYY HH:mm')}
                    </Descriptions.Item>
                    {companyDetails.updatedAt && (
                        <Descriptions.Item label="Последнее обновление">
                            {dayjs(companyDetails.updatedAt).format('DD.MM.YYYY HH:mm')}
                        </Descriptions.Item>
                    )}
                </Descriptions>

                {companyDetails.contractStats && (
                    <>
                        <Divider>Статистика по договорам</Divider>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic
                                    title="Всего"
                                    value={companyDetails.contractStats.totalContracts}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Подписанные"
                                    value={companyDetails.contractStats.signedContracts}
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Черновики"
                                    value={companyDetails.contractStats.draftContracts}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Ожидают"
                                    value={companyDetails.contractStats.pendingContracts}
                                    valueStyle={{ color: '#faad14' }}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Card>

            {/* Договоры компании */}
            <Card>
                <Title level={4}>
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    Подписанные договоры ({contracts.length})
                </Title>

                <Table
                    columns={contractsColumns}
                    dataSource={contracts}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `Всего: ${total} договоров`,
                    }}
                />
            </Card>

            {/* Модальное окно подписания */}
            <Modal
                title="Подписание договора от имени поставщика"
                open={signingModalVisible}
                onCancel={() => {
                    setSigningModalVisible(false);
                    setSigningContract(null);
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setSigningModalVisible(false);
                            setSigningContract(null);
                        }}
                    >
                        Отмена
                    </Button>,
                    <Button
                        key="sign"
                        type="primary"
                        loading={signingLoading}
                        onClick={handleSignWithNCALayer}
                        disabled={ncaLayerStatus !== 'available' || !selectedCertificate}
                    >
                        Подписать через NCA Layer
                    </Button>
                ]}
            >
                {signingContract && (
                    <div>
                        <p><strong>Договор:</strong> {signingContract.contractNumber}</p>
                        <p><strong>Компания:</strong> {signingContract.companyName}</p>
                        <p><strong>Статус:</strong>
                            <Tag color={getStatusColor(signingContract.status)} style={{ marginLeft: 8 }}>
                                {getStatusText(signingContract.status)}
                            </Tag>
                        </p>

                        <Divider />

                        {/* Статус NCA Layer */}
                        {ncaLayerStatus === 'checking' && (
                            <Alert
                                message="Проверка подключения к NCA Layer..."
                                type="info"
                                icon={<Spin size="small" />}
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {ncaLayerStatus === 'unavailable' && (
                            <Alert
                                message="NCA Layer недоступен"
                                description="Проверьте подключение к интернету и убедитесь, что NCA Layer установлен и запущен."
                                type="error"
                                icon={<ExclamationCircleOutlined />}
                                style={{ marginBottom: 16 }}
                                action={
                                    <Button size="small" onClick={checkNCALayer}>
                                        Повторить проверку
                                    </Button>
                                }
                            />
                        )}

                        {ncaLayerStatus === 'available' && (
                            <div>
                                <Alert
                                    message="NCA Layer подключен"
                                    description="Готов к подписанию документов"
                                    type="success"
                                    style={{ marginBottom: 16 }}
                                />

                                {/* Выбор сертификата */}
                                {certificates.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>Выберите сертификат:</Text>
                                        <div style={{ marginTop: 8 }}>
                                            {certificates.map((cert) => (
                                                <div
                                                    key={cert.path}
                                                    style={{
                                                        padding: '8px 12px',
                                                        border: selectedCertificate === cert.path ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        marginBottom: '4px',
                                                        backgroundColor: selectedCertificate === cert.path ? '#f6ffed' : '#fff'
                                                    }}
                                                    onClick={() => setSelectedCertificate(cert.path)}
                                                >
                                                    <Text strong>{cert.alias}</Text>
                                                    <br />
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        {cert.subject}
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '4px' }}>
                            <Text type="secondary">
                                Договор будет подписан от имени поставщика с использованием сертификата NCA Layer.
                                Убедитесь, что у вас установлен и настроен NCA Layer.
                            </Text>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Модальное окно просмотра подписей */}
            <Modal
                title={
                    <div>
                        <SafetyCertificateOutlined style={{ marginRight: '8px' }} />
                        Цифровые подписи договора {viewingContract?.contractNumber}
                    </div>
                }
                open={signaturesModalVisible}
                onCancel={() => {
                    setSignaturesModalVisible(false);
                    setViewingContract(null);
                    setContractSignatures([]);
                }}
                footer={[
                    <Button
                        key="close"
                        onClick={() => {
                            setSignaturesModalVisible(false);
                            setViewingContract(null);
                            setContractSignatures([]);
                        }}
                    >
                        Закрыть
                    </Button>
                ]}
                width={800}
            >
                {contractSignatures.length > 0 ? (
                    <div>
                        {contractSignatures.map((signature, index) => (
                            <Card key={signature.id} style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <div>
                                        <Text strong style={{ fontSize: '16px' }}>
                                            {signature.signerTypeDisplay}
                                        </Text>
                                        <Tag color={signature.signerType === 'SUPPLIER' ? 'blue' : 'green'} style={{ marginLeft: '8px' }}>
                                            {signature.signerType === 'SUPPLIER' ? 'Поставщик' : 'Покупатель'}
                                        </Tag>
                                    </div>
                                    <Tag color={signature.isValid ? 'green' : 'red'}>
                                        {signature.isValid ? 'Действительна' : 'Недействительна'}
                                    </Tag>
                                </div>

                                <Descriptions bordered column={2} size="small">
                                    <Descriptions.Item label="Подписант" span={2}>
                                        <Text strong>{signature.signerName}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Дата подписания">
                                        {dayjs(signature.signatureDate).format('DD.MM.YYYY HH:mm')}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Хэш подписи">
                                        <Text code style={{ fontSize: '12px' }}>
                                            {signature.signatureHash}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Серийный номер сертификата">
                                        <Text code>{signature.certificateSerial}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Организация">
                                        {signature.certificateOrganization}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Действителен с">
                                        {signature.certificateValidFrom ? dayjs(signature.certificateValidFrom).format('DD.MM.YYYY') : 'Не указано'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Действителен до">
                                        {signature.certificateValidTo ? dayjs(signature.certificateValidTo).format('DD.MM.YYYY') : 'Не указано'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Статус проверки" span={2}>
                                        <Text type={signature.isValid ? 'success' : 'danger'}>
                                            {signature.validationStatus}
                                        </Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <SafetyCertificateOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                        <Text type="secondary">Цифровые подписи не найдены</Text>
                    </div>
                )}
            </Modal>
        </div>
    );
};
